import React, {useState} from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Theme} from '../../../theme/lightTheme';
import {Animated} from 'react-native';
import Icon from '../../icon';
import {RefreshChatBubbleProps} from '../../../interfaces/components/refreshChatBubble';

const Container = styled.TouchableOpacity`
  justify-content: center;
  align-items: center;
`;

/**
 * A `Refresh` component that provides an interactive refresh button with a rotation animation. Upon pressing the button,
 * it triggers a 360-degree rotation animation to indicate a refresh action and optionally executes a provided onPress function.
 *
 * @param {Object} props - The properties passed to the Refresh component.
 * @param {Function} props.onPress - The function to execute when the refresh button is pressed. This function is intended
 *                                    for performing the actual refresh operation (e.g., fetching new data).
 * @param {Object} props.style - Custom style properties to apply to the container of the refresh button. Allows for
 *                               customization of the button's positioning and appearance within its parent component.
 * @returns {React.ReactElement} A component representing a refresh button with an animation effect. The button uses an
 *                               icon to visually represent the refresh action and supports custom styling.
 */
const RefreshChatBubble: React.FC<RefreshChatBubbleProps> = ({
  onPress,
  style,
}: RefreshChatBubbleProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const rotateAnim = useState(new Animated.Value(0))[0]; // Initial rotation value

  const startRotation = () => {
    rotateAnim.setValue(0); // Reset the animation value to 0
    Animated.timing(rotateAnim, {
      toValue: 1, // Animate to rotation of 1 (full circle)
      duration: 1000, // Animation should take 1000ms
      useNativeDriver: true, // Use native driver for better performance
    }).start();
  };

  const rotation = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'], // Interpolate rotation from 0 to 360 degrees
  });
  const onPressHandler = () => {
    if (onPress) {
      onPress();
    }
    startRotation();
  };
  return (
    <Container style={style} onPress={onPressHandler}>
      <Animated.View style={{transform: [{rotate: rotation}]}}>
        <Icon
          name={'refresh-circle'}
          type={'Ionicons'}
          color={theme.primary}
          size={theme.text.s2}
        />
      </Animated.View>
    </Container>
  );
};
/**
 * Default props for the Refresh component.
 *
 * @type {RefreshProps}
 */
RefreshChatBubble.defaultProps = {
  style: {},
};
export default RefreshChatBubble;
